/**
 * Dispatch health: worst city first.
 *
 * **This screen exists to show trouble, not health.** The server orders it by acceptance
 * ascending; the client does not re-sort. A board sorted by city name makes an operator read
 * every row to find the one that needs them.
 *
 * **A city that dispatched nothing is absent, not zero.** A rate over zero waves is not zero — it
 * is unknown, and a red 0% against a city that simply had a quiet week sends somebody hunting for
 * a fault that is not there.
 *
 * **The Gini column is the fairness check.** It is the same number the matcher's own fairness
 * signal reads: 0 is offers spread evenly across the roster, 1 is one pandit taking everything.
 * A city with high acceptance and a high Gini is a city where three people are doing all the work
 * and the rest are about to churn out.
 */
import { useState } from 'react';
import { Note, StatusChip } from '../components/primitives';
import { DataTable } from '../components/data';
import { Select } from '../components/forms';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { Section } from '../components/kit';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import { useDispatchHealth, type CityDispatchHealth } from '../lib/api/hooks/dispatch';

const WINDOWS = ['7 days', '30 days', '90 days'] as const;

export function windowDays(label: string): number {
  return Number(label.split(' ')[0]);
}

/** The tint lines the API serves from settings (admin doc 08 §4: `dispatch.health_*`). */
export interface HealthLines {
  acceptance_warn: number;
  acceptance_alarm: number;
  exhaustion_warn: number;
}

/**
 * How alarming an acceptance rate is, against the lines ops set. They are settings rather than
 * constants here, so the health screen and the board's strip draw the same line and moving it
 * needs no release.
 */
export function acceptanceTone(
  rate: number | null | undefined,
  lines: HealthLines,
): 'neutral' | 'error' | 'warn' | 'success' {
  /* Absent and null mean the same thing — no waves went out — and the generated type
     carries both because the field has a default. Collapsing them here keeps every
     caller from having to remember which one the server sent. */
  if (rate === null || rate === undefined) return 'neutral';
  if (rate < lines.acceptance_alarm) return 'error';
  if (rate < lines.acceptance_warn) return 'warn';
  return 'success';
}

/** In wave trouble: acceptance under the red line, or enough bookings that ran out. */
export function inWaveTrouble(city: CityDispatchHealth, lines: HealthLines): boolean {
  return acceptanceTone(city.acceptance_rate, lines) === 'error' || city.exhausted >= lines.exhaustion_warn;
}

/**
 * How concentrated the offers were.
 *
 * Above 0.5 means most offers went to a handful of pandits, which is a supply problem that has
 * not surfaced yet: the acceptance rate can look fine right up until the three people carrying
 * the city stop answering.
 */
export function giniTone(gini: number | undefined): 'error' | 'warn' | 'success' {
  if (gini === undefined) return 'success';
  if (gini > 0.5) return 'error';
  if (gini > 0.3) return 'warn';
  return 'success';
}

export function asPercent(rate: number | null | undefined): string {
  return rate === null || rate === undefined ? '—' : `${Math.round(rate * 100)}%`;
}

/** "1m 40s" reads faster than "100" when the column is a duration. */
export function asDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—';
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

export function DispatchHealth() {
  const { go } = useConsole();
  const [window, setWindow] = useState<string>(WINDOWS[0]);
  const health = useDispatchHealth(windowDays(window));

  const rows: CityDispatchHealth[] = health.data?.items ?? [];
  const lines = health.data;
  const flagged = lines ? rows.filter((c) => inWaveTrouble(c, lines)) : [];

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-md)' }}>
        <Select
          density="admin"
          label="Window"
          value={window}
          options={[...WINDOWS]}
          onChange={setWindow}
          style={{ width: 160 }}
        />
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
          Ordered worst first by the server — this screen shows trouble, not health.
        </span>
      </div>

      {health.isPending && <LoadingState message="Reading dispatch health…" />}
      {health.isError && (
        <ErrorState
          title="Could not read dispatch health"
          cause={errorMessage(health.error)}
        />
      )}
      {health.isSuccess && rows.length === 0 && (
        <EmptyState
          icon="activity"
          title="Nothing was dispatched in this window"
          body="No waves went out, so there is no acceptance rate to report — that is a fact about demand, not about supply."
          inline
        />
      )}

      {rows.length > 0 && (
        <>
          {flagged.length > 0 && (
            <Section
              title={`${flagged.length} ${flagged.length === 1 ? 'city needs' : 'cities need'} attention`}
              note="Low acceptance, or bookings that ran out of supply entirely"
            >
              <p style={{ margin: 0, fontSize: 'var(--fs-admin-ui)', color: 'var(--c-ink2)' }}>
                {flagged.map((c) => c.city_name).join(', ')}
              </p>
            </Section>
          )}

          <DataTable
            density="compact"
            onRowClick={() => go('dispatch')}
            columns={[
              { key: 'city', label: 'City' },
              { key: 'bookings', label: 'Dispatched', align: 'right', mono: true },
              { key: 'offers', label: 'Offers', align: 'right', mono: true },
              { key: 'accept', label: 'Accepted' },
              { key: 'latency', label: 'Median to accept', align: 'right', mono: true },
              { key: 'exhausted', label: 'Ran out', align: 'right', mono: true },
              { key: 'gini', label: 'Offer spread' },
            ]}
            rows={rows.map((c) => ({
              id: c.city_id,
              city: c.city_name,
              bookings: c.bookings_dispatched,
              offers: c.offers_sent,
              accept: (
                <StatusChip
                  tone={acceptanceTone(c.acceptance_rate, lines!)}
                  label={`${asPercent(c.acceptance_rate)} of ${c.offers_sent}`}
                />
              ),
              latency: asDuration(c.median_seconds_to_accept),
              exhausted: c.exhausted,
              gini: (
                <StatusChip
                  tone={giniTone(c.offer_gini)}
                  label={`${(c.offer_gini ?? 0).toFixed(2)} concentration`}
                />
              ),
            }))}
          />
        </>
      )}

      <Note>
        Offer spread is the Gini coefficient of offers across each city&apos;s roster — 0 is even,
        1 is one pandit taking everything. It is the same number the matcher&apos;s own fairness
        signal reads.
      </Note>
    </>
  );
}
